/**
 * dictionary-options — budowa opcji `SearchableSelect` dla pickerów słowników
 * (`DictionaryPicker` tenanta, `GlobalDictionaryPicker` panelu ITE).
 *
 * Plakietki (badges) oznaczają WYŁĄCZNIE wyjątki — zwykły lokalny słownik ręczny nie dostaje
 * żadnej. Kolejność plakietek (tenant): globalny, systemowy, SQL, zewnętrzny, ERP. Tony:
 * globalny = accent, systemowy = neutral, SQL = warning, zewnętrzny = warning, ERP = success.
 * Plakietki SQL i zewnętrzny wykluczają się (SQL = `sourceKind==='external'` bez `sourceKey`,
 * zewnętrzny = z `sourceKey` z rejestru źródeł).
 *
 * Kolejność opcji = kolejność z API (bez ponownego sortowania). `searchText` = kod słownika +
 * etykiety plakietek, więc wyszukiwarka znajduje słownik także po kodzie i po typie.
 */

import type { SearchableSelectBadge, SearchableSelectOption } from '@/components/SearchableSelect';
import type { DictionarySummary } from '@/hooks/useDictionary';
import type { AdminGlobalDictionary } from '@/lib/global-dictionaries/api';

/** Funkcja tłumacząca (podzbiór `TFunction` — wystarczy klucz → tekst). */
export type DictionaryOptionsTranslate = (key: string) => string;

function sourceBadges(
  sourceKind: string | undefined,
  sourceKey: string | null | undefined,
  t: DictionaryOptionsTranslate,
): SearchableSelectBadge[] {
  const badges: SearchableSelectBadge[] = [];
  if (sourceKind === 'external' && !sourceKey) {
    badges.push({ label: t('common.dictionaryPicker.badge.sql'), tone: 'warning' });
  } else if (sourceKind === 'external') {
    badges.push({ label: t('common.dictionaryPicker.badge.external'), tone: 'warning' });
  }
  return badges;
}

function toOption(
  code: string,
  name: string,
  description: string | null,
  badges: SearchableSelectBadge[],
): SearchableSelectOption {
  const trimmedDescription = description?.trim();
  return {
    value: code,
    label: name,
    description: trimmedDescription ? trimmedDescription : undefined,
    badges: badges.length > 0 ? badges : undefined,
    searchText: [code, ...badges.map((b) => b.label)].join(' '),
  };
}

/** Opcje dla pickera TENANTA (lista scalona lokalne + globalne). */
export function buildTenantDictionaryOptions(
  dictionaries: ReadonlyArray<DictionarySummary>,
  t: DictionaryOptionsTranslate,
): SearchableSelectOption[] {
  return dictionaries.map((d) => {
    const badges: SearchableSelectBadge[] = [];
    if (d.origin === 'global') {
      badges.push({ label: t('common.dictionaryPicker.badge.global'), tone: 'accent' });
    }
    if (d.isSystem) {
      badges.push({ label: t('common.dictionaryPicker.badge.system'), tone: 'neutral' });
    }
    badges.push(...sourceBadges(d.sourceKind, d.sourceKey, t));
    if (d.source === 'erp') {
      badges.push({ label: t('common.dictionaryPicker.badge.erp'), tone: 'success' });
    }
    return toOption(d.code, d.name, d.description, badges);
  });
}

/**
 * Gdy zapisana wartość (`value`) nie występuje wśród opcji (słownik wycofany z katalogu, usunięty
 * albo niedostępny dla tenanta), dokłada opcję zastępczą z kodem jako etykietą i plakietką
 * „niedostępny" — inaczej trigger pokazywałby placeholder „brak" przy jednocześnie widocznym „X".
 * `null` albo wartość obecna na liście → lista bez zmian (ta sama referencja). Wołający nie
 * stosuje tego w czasie ładowania listy (wtedy brak opcji nie oznacza niedostępności).
 */
export function withUnavailableValueOption(
  options: SearchableSelectOption[],
  value: string | null,
  t: DictionaryOptionsTranslate,
): SearchableSelectOption[] {
  if (value === null || options.some((opt) => opt.value === value)) return options;
  return [
    ...options,
    {
      value,
      label: value,
      badges: [{ label: t('common.dictionaryPicker.badge.unavailable'), tone: 'warning' }],
    },
  ];
}

/**
 * Opcje dla pickera panelu ITE (katalog globalny). Wszystkie słowniki są globalne, więc bez
 * plakietki „globalny"; filtrowanie po `status` robi wołający.
 */
export function buildGlobalDictionaryOptions(
  dictionaries: ReadonlyArray<AdminGlobalDictionary>,
  t: DictionaryOptionsTranslate,
): SearchableSelectOption[] {
  return dictionaries.map((d) =>
    toOption(d.code, d.name, d.description, sourceBadges(d.sourceKind, d.sourceKey, t)),
  );
}
